import { z } from "zod";

// ── Schemas ────────────────────────────────────────────────

export const OverheadActual = z.object({
  contextResend: z.number(),
  toolCalls: z.number(),
  thinking: z.number(),
  errorRecovery: z.number(),
});
export type OverheadActual = z.infer<typeof OverheadActual>;

export const CalibrationDataPoint = z.object({
  taskType: z.string(),
  complexity: z.string(),
  model: z.string(),
  provider: z.string(),
  estimatedTokens: z.number(),
  actualTokens: z.number(),
  accuracy: z.number(),
  overheadActual: OverheadActual,
  timestamp: z.number(),
});
export type CalibrationDataPoint = z.infer<typeof CalibrationDataPoint>;

export const CommunityProfile = z.object({
  taskType: z.string(),
  complexity: z.string(),
  model: z.string(),
  provider: z.string(),
  avgEstimatedTokens: z.number(),
  avgActualTokens: z.number(),
  avgAccuracy: z.number(),
  avgOverhead: OverheadActual,
  dataPoints: z.number(),
  confidence: z.number(),
  stdDevAccuracy: z.number(),
  lastUpdated: z.number(),
});
export type CommunityProfile = z.infer<typeof CommunityProfile>;

const ContributionEnvelope = z.object({
  version: z.literal(1),
  schemaVersion: z.literal("1.0"),
  dataPoints: z.array(CalibrationDataPoint),
  exportedAt: z.number(),
});

const CommunityProfileEnvelope = z.object({
  version: z.number(),
  schemaVersion: z.string(),
  profiles: z.array(CommunityProfile),
});

// ── Fields that must NEVER appear in shared data ───────────
// Enforced by schema: CalibrationDataPoint only contains numeric
// and categorical fields. No strings for code, prompts, file
// contents, project names, API keys, or business details.

/**
 * Input shape for calibrated profiles (from .fathom/calibrated-profiles.yaml or store).
 * This is the raw calibration data before anonymization.
 */
export interface CalibratedProfile {
  taskType: string;
  complexity: string;
  model: string;
  provider: string;
  estimatedTokens: number;
  actualTokens: number;
  overheadMultipliers?: {
    contextResend?: number;
    toolCalls?: number;
    thinking?: number;
    errorRecovery?: number;
  };
  dataPoints?: number;
  /** Optional — will be stripped during preparation */
  projectName?: string;
  featureName?: string;
  filePaths?: string[];
  prompts?: string[];
}

// ── Functions ──────────────────────────────────────────────

/**
 * Takes the user's calibrated profiles, strips any identifying information,
 * and returns anonymized data points ready to share.
 */
export function prepareContribution(
  calibrationData: CalibratedProfile[],
): CalibrationDataPoint[] {
  const points: CalibrationDataPoint[] = [];

  for (const profile of calibrationData) {
    if (!profile.taskType || !profile.complexity || !profile.model || !profile.provider) {
      continue; // Skip incomplete profiles
    }
    if (profile.estimatedTokens <= 0 || profile.actualTokens <= 0) {
      continue; // Skip invalid token counts
    }

    const accuracy = profile.actualTokens / profile.estimatedTokens;

    const point: CalibrationDataPoint = {
      taskType: profile.taskType,
      complexity: profile.complexity,
      model: profile.model,
      provider: profile.provider,
      estimatedTokens: profile.estimatedTokens,
      actualTokens: profile.actualTokens,
      accuracy,
      overheadActual: {
        contextResend: profile.overheadMultipliers?.contextResend ?? 0,
        toolCalls: profile.overheadMultipliers?.toolCalls ?? 0,
        thinking: profile.overheadMultipliers?.thinking ?? 0,
        errorRecovery: profile.overheadMultipliers?.errorRecovery ?? 0,
      },
      timestamp: Date.now(),
    };

    // Validate against schema — ensures no extra fields leak through
    const parsed = CalibrationDataPoint.safeParse(point);
    if (parsed.success) {
      points.push(parsed.data);
    }
  }

  return points;
}

/**
 * Returns a human-readable preview of exactly what will be shared.
 * Format: markdown table with task type, complexity, model, accuracy, token counts.
 */
export function previewContribution(dataPoints: CalibrationDataPoint[]): string {
  if (dataPoints.length === 0) {
    return "No data points to share.";
  }

  const lines: string[] = [];
  lines.push(
    "| Task Type       | Complexity | Model              | Accuracy | Tokens (est → actual) |",
  );
  lines.push(
    "| --------------- | ---------- | ------------------ | -------- | --------------------- |",
  );

  for (const dp of dataPoints) {
    const accuracyPct = `${Math.round(dp.accuracy * 100)}%`;
    const tokens = `${dp.estimatedTokens.toLocaleString()} → ${dp.actualTokens.toLocaleString()}`;
    const modelShort = dp.model.length > 18 ? dp.model.slice(0, 18) : dp.model;

    lines.push(
      `| ${pad(dp.taskType, 15)} | ${pad(dp.complexity, 10)} | ${pad(modelShort, 18)} | ${pad(accuracyPct, 8)} | ${pad(tokens, 21)} |`,
    );
  }

  return lines.join("\n");
}

function pad(s: string, len: number): string {
  return s.length >= len ? s : s + " ".repeat(len - s.length);
}

/**
 * Merges new data points into community profiles.
 * Updates running averages, data point counts, confidence.
 * Rejects outliers > 3 standard deviations from mean.
 */
export function aggregateContributions(
  existing: CommunityProfile[],
  incoming: CalibrationDataPoint[],
): CommunityProfile[] {
  // Index existing profiles by composite key
  const profileMap = new Map<string, CommunityProfile>();
  for (const profile of existing) {
    const key = profileKey(profile);
    profileMap.set(key, { ...profile });
  }

  for (const dp of incoming) {
    const key = `${dp.taskType}|${dp.complexity}|${dp.model}|${dp.provider}`;
    const profile = profileMap.get(key);

    if (profile) {
      // Outlier detection: reject if accuracy is > 3 stddev from mean
      if (profile.stdDevAccuracy > 0 && profile.dataPoints >= 3) {
        const zScore = Math.abs(dp.accuracy - profile.avgAccuracy) / profile.stdDevAccuracy;
        if (zScore > 3) {
          continue; // Reject outlier
        }
      }

      // Update running averages using incremental formula
      const n = profile.dataPoints;
      const n1 = n + 1;

      // Update stddev using Welford's online algorithm
      const oldMean = profile.avgAccuracy;
      const newMeanAccuracy = (oldMean * n + dp.accuracy) / n1;
      const oldM2 = profile.stdDevAccuracy * profile.stdDevAccuracy * n;
      const newM2 = Math.max(0, oldM2 + (dp.accuracy - oldMean) * (dp.accuracy - newMeanAccuracy));

      profile.avgEstimatedTokens = (profile.avgEstimatedTokens * n + dp.estimatedTokens) / n1;
      profile.avgActualTokens = (profile.avgActualTokens * n + dp.actualTokens) / n1;
      profile.avgAccuracy = newMeanAccuracy;
      profile.avgOverhead = {
        contextResend: (profile.avgOverhead.contextResend * n + dp.overheadActual.contextResend) / n1,
        toolCalls: (profile.avgOverhead.toolCalls * n + dp.overheadActual.toolCalls) / n1,
        thinking: (profile.avgOverhead.thinking * n + dp.overheadActual.thinking) / n1,
        errorRecovery: (profile.avgOverhead.errorRecovery * n + dp.overheadActual.errorRecovery) / n1,
      };
      profile.dataPoints = n1;
      const rawStdDev = n1 > 1 ? Math.sqrt(newM2 / n1) : 0;
      profile.stdDevAccuracy = isFinite(rawStdDev) ? rawStdDev : 0;
      profile.confidence = Math.min(n1 / 30, 1); // 30 data points = full confidence
      profile.lastUpdated = dp.timestamp;
    } else {
      // Create new profile from this data point
      profileMap.set(key, {
        taskType: dp.taskType,
        complexity: dp.complexity,
        model: dp.model,
        provider: dp.provider,
        avgEstimatedTokens: dp.estimatedTokens,
        avgActualTokens: dp.actualTokens,
        avgAccuracy: dp.accuracy,
        avgOverhead: { ...dp.overheadActual },
        dataPoints: 1,
        confidence: 1 / 30,
        stdDevAccuracy: 0,
        lastUpdated: dp.timestamp,
      });
    }
  }

  return Array.from(profileMap.values());
}

function profileKey(p: { taskType: string; complexity: string; model: string; provider: string }): string {
  return `${p.taskType}|${p.complexity}|${p.model}|${p.provider}`;
}

/**
 * Serializes calibration data points to JSON for transport.
 * Includes version and schema version for forward compatibility.
 */
export function exportContribution(dataPoints: CalibrationDataPoint[]): string {
  const envelope = {
    version: 1 as const,
    schemaVersion: "1.0" as const,
    dataPoints,
    exportedAt: Date.now(),
  };

  return JSON.stringify(envelope, null, 2);
}

/**
 * Deserializes community profiles from JSON. Validates against schema.
 */
export function importCommunityProfiles(json: string): CommunityProfile[] {
  const parsed = JSON.parse(json);
  const envelope = CommunityProfileEnvelope.parse(parsed);
  return envelope.profiles;
}
